import type { Maybe } from '@adornis/base/utilTypes.js';
import { Float, Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import { type PartialEntityData } from '@adornis/baseql/entities/adornisEntity.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { loadMongoData } from '@adornis/baseql/server/data-loader.js';
import { Mitarbeiter } from '@chancenwerk/master-data-management/users/entities/Mitarbeiter.js';
import { DateTime } from 'luxon';
import { KPFPublicInformationAPI } from '../../public-access/api/KPFPublicInformationAPI.js';
import { WorkTrackingAuslage } from './WorkTrackingAuslage.js';
import { WorkTrackingTaetigkeit } from './WorkTrackingTaetigkeit.js';
import { combinedTrackingTaetigkeitenState, WorkTrackingTaetigkeitenState } from './WorkTrackingTaetigkeitenState.js';

/**
 * Entity to hold a work tracking activity for one month of a contractor.
 */
@Entity()
export class WorkTrackingEntry extends MongoEntity {
  static override _class = 'WorkTrackingEntry';
  static override _collectionName = 'work-tracking-entry';

  constructor(doc: PartialEntityData<WorkTrackingEntry>) {
    super(doc);
  }

  public static async addMentionedKPF(userID: string, kpfID: string, date: Date | DateTime) {
    const selector = WorkTrackingEntry._monthSelector(userID, date);
    const col = await getRawCollection<WorkTrackingEntry>(WorkTrackingEntry._collectionName);
    return col.updateOne(selector as any, { $addToSet: { mentionedKpfIDs: kpfID } });
  }

  public static async removeMentionedKPF(userID: string, kpfID: string, date: Date | DateTime) {
    const selector = WorkTrackingEntry._monthSelector(userID, date);
    const col = await getRawCollection<WorkTrackingEntry>(WorkTrackingEntry._collectionName);
    return col.updateOne(selector as any, { $pull: { mentionedKpfIDs: kpfID } });
  }

  private static _monthSelector(userID: Maybe<string>, date: Date | DateTime) {
    const startOfMonth = date instanceof Date ? DateTime.fromJSDate(date).startOf('month') : date.startOf('month');
    const endOfMonth = date instanceof Date ? DateTime.fromJSDate(date).endOf('month') : date.endOf('month');

    return {
      userID,
      date: {
        $gte: startOfMonth.toJSDate(),
        $lte: endOfMonth.toJSDate(),
      },
    };
  }

  @Field(type => DateTime)
  date: Maybe<DateTime>;

  @DataLoadedField(type => Mitarbeiter, ({ userID }) => userID)
  user: Maybe<Mitarbeiter>;

  @Field(type => String)
  userID: Maybe<string>;

  @Field(type => Int, {
    resolve: function (this: PartialEntityData<WorkTrackingEntry>) {
      return async () => {
        if (!this.date) {
          return null;
        }
        const selector = WorkTrackingEntry._monthSelector(this.userID, this.date);
        const colT = await getRawCollection<WorkTrackingTaetigkeit>(WorkTrackingTaetigkeit._collectionName);
        const colA = await getRawCollection<WorkTrackingAuslage>(WorkTrackingAuslage._collectionName);
        return (await colT.countDocuments(selector)) + (await colA.countDocuments(selector));
      };
    },
  })
  entryCount: Maybe<number>;

  @Field(type => DateTime, {
    resolve: function (this: PartialEntityData<WorkTrackingEntry>) {
      return async () => {
        if (!this.date) {
          return null;
        }

        const selector = WorkTrackingEntry._monthSelector(this.userID, this.date);
        const options = {
          projection: {
            changes: 1,
          },
        };

        const changes = [
          ...(await loadMongoData(WorkTrackingTaetigkeit, selector, options)).flatMap(t => t.changes),
          ...(await loadMongoData(WorkTrackingAuslage, selector, options)).flatMap(t => t.changes),
        ];
        const newest = changes
          .filter(Boolean)
          .filter(c => c?.date)
          .map(c => c as unknown as { date: Date })
          .sort((a, b) => b.date.getTime() - a.date.getTime())
          .at(0);

        return newest ? DateTime.fromJSDate(newest?.date) : null;
      };
    },
  })
  lastChange: Maybe<DateTime>;

  @Field(type => String, {
    resolve: function (this: PartialEntityData<WorkTrackingEntry>) {
      return async () => {
        if (!this.date) {
          return WorkTrackingTaetigkeitenState.OPEN;
        }

        const selector = WorkTrackingEntry._monthSelector(this.userID, this.date);
        const options = {
          projection: {
            stateContractor: 1,
            stateResponsible: 1,
          },
        };

        const taetikeiten = (await loadMongoData(WorkTrackingTaetigkeit, selector, options)).flatMap(t => [
          t.stateContractor,
          t.stateResponsible,
        ]);
        if (combinedTrackingTaetigkeitenState(taetikeiten) === WorkTrackingTaetigkeitenState.QUESTION) {
          return WorkTrackingTaetigkeitenState.QUESTION;
        }

        const auslagen = (await loadMongoData(WorkTrackingAuslage, selector, options)).flatMap(t => [
          t.stateContractor,
          t.stateResponsible,
        ]);
        if (combinedTrackingTaetigkeitenState(auslagen) === WorkTrackingTaetigkeitenState.QUESTION) {
          return WorkTrackingTaetigkeitenState.QUESTION;
        }

        return combinedTrackingTaetigkeitenState([...taetikeiten, ...auslagen]);
      };
    },
  })
  state: Maybe<WorkTrackingTaetigkeitenState>;

  @Field(type => String, {
    resolve: function (this: PartialEntityData<WorkTrackingEntry>) {
      return async () => {
        if (!this.date) {
          return WorkTrackingTaetigkeitenState.OPEN;
        }

        const selector = WorkTrackingEntry._monthSelector(this.userID, this.date);
        const options = {
          projection: {
            stateContractor: 1,
            stateResponsible: 1,
          },
        };

        const taetikeiten = (await loadMongoData(WorkTrackingTaetigkeit, selector, options)).map(
          t => t.stateContractor,
        );
        if (combinedTrackingTaetigkeitenState(taetikeiten) === WorkTrackingTaetigkeitenState.QUESTION) {
          return WorkTrackingTaetigkeitenState.QUESTION;
        }

        const auslagen = (await loadMongoData(WorkTrackingAuslage, selector, options)).map(t => t.stateContractor);
        if (combinedTrackingTaetigkeitenState(auslagen) === WorkTrackingTaetigkeitenState.QUESTION) {
          return WorkTrackingTaetigkeitenState.QUESTION;
        }

        return combinedTrackingTaetigkeitenState([...taetikeiten, ...auslagen]);
      };
    },
  })
  stateContractor: Maybe<WorkTrackingTaetigkeitenState>;

  @Field(type => String, {
    resolve: function (this: PartialEntityData<WorkTrackingEntry>) {
      return async () => {
        if (!this.date) {
          return WorkTrackingTaetigkeitenState.OPEN;
        }

        const selector = WorkTrackingEntry._monthSelector(this.userID, this.date);
        const options = {
          projection: {
            stateContractor: 1,
            stateResponsible: 1,
          },
        };

        const taetikeiten = (await loadMongoData(WorkTrackingTaetigkeit, selector, options)).map(
          t => t.stateResponsible,
        );
        if (combinedTrackingTaetigkeitenState(taetikeiten) === WorkTrackingTaetigkeitenState.QUESTION) {
          return WorkTrackingTaetigkeitenState.QUESTION;
        }

        const auslagen = (await loadMongoData(WorkTrackingAuslage, selector, options)).map(t => t.stateResponsible);
        if (combinedTrackingTaetigkeitenState(auslagen) === WorkTrackingTaetigkeitenState.QUESTION) {
          return WorkTrackingTaetigkeitenState.QUESTION;
        }

        return combinedTrackingTaetigkeitenState([...taetikeiten, ...auslagen]);
      };
    },
  })
  stateResponsible: Maybe<WorkTrackingTaetigkeitenState>;

  @Field(type => Float, {
    resolve: function (this: PartialEntityData<WorkTrackingEntry>) {
      return async () => {
        if (!this.date) {
          return 0;
        }

        const selector = WorkTrackingEntry._monthSelector(this.userID, this.date);
        const options = {
          projection: {
            money: 1,
          },
        };

        const taetikeiten = (await loadMongoData(WorkTrackingTaetigkeit, selector, options))
          .filter(t => !t.disabled)
          .map(t => t.money);
        const auslagen = (await loadMongoData(WorkTrackingAuslage, selector, options)).map(t => t.money);

        return (
          (taetikeiten?.reduce((acc, cur) => (acc ?? 0) + (cur ?? 0), 0) ?? 0) +
          (auslagen?.reduce((acc, cur) => (acc ?? 0) + (cur ?? 0), 0) ?? 0)
        );
      };
    },
  })
  moneySum: Maybe<number>;

  @Field(type => Float, {
    resolve: function (this: PartialEntityData<WorkTrackingEntry>) {
      return async () => {
        if (!this.date) {
          return 0;
        }

        const selector = WorkTrackingEntry._monthSelector(this.userID, this.date);
        const options = {
          projection: {
            money: 1,
          },
        };

        const auslagen = (await loadMongoData(WorkTrackingAuslage, selector, options)).map(t => t.money);

        return auslagen?.reduce((acc, cur) => (acc ?? 0) + (cur ?? 0), 0) ?? 0;
      };
    },
  })
  moneyAuslagenSum: Maybe<number>;

  @Field(type => [String], { default: val => val ?? [] })
  mentionedKpfIDs: Maybe<string[]>;

  @Field(type => [String], {
    resolve: function (this: PartialEntityData<WorkTrackingEntry>) {
      return async () => {
        if (!this.date) {
          return [];
        }

        const selector = WorkTrackingEntry._monthSelector(this.userID, this.date);
        const options = {
          projection: {
            kpfID: 1,
          },
        };

        const taetikeiten = (await loadMongoData(WorkTrackingTaetigkeit, selector, options))
          .map(t => t.kpfID)
          .filter(Boolean) as string[];
        const auslagen = (await loadMongoData(WorkTrackingAuslage, selector, options))
          .map(t => t.kpfID)
          .filter(Boolean) as string[];

        const ids = [...new Set([...taetikeiten, ...auslagen])];
        const kpfs = await KPFPublicInformationAPI.getByID(ids)({
          name: 1,
          kuerzel: 1,
          produkt: { _class: 1, kuerzel: 1 },
          school: { name: 1, kuerzel: 1 },
        });

        return ids.map(id => {
          const found = kpfs.find(kpf => kpf._id === id);
          if (!found) return '';
          return `${found.kuerzel} ${found.school?.name}`;
        });
      };
    },
  })
  mentionedKpfNames: Maybe<string[]>;

  get mentionedKpfs() {
    const kpfs =
      this.mentionedKpfIDs?.map((id, i) => ({
        id: id.split('_')[1] ?? '',
        name: this.mentionedKpfNames?.[i] ?? '',
      })) ?? [];
    return [...new Set(kpfs.map(kpf => kpf.id))].map(id => kpfs.find(kpf => kpf.id === id));
  }
}
